import { getAuthToken } from '../utils/authUtils';

export type AuthTokenGetter = () => Promise<string | null>;

let _tokenGetter: AuthTokenGetter | null = null;

/**
 * Register the Auth0/env token resolver (once from AuthStateMonitor).
 *
 * Example: setAuthTokenGetter(async () => getAuthToken(getIdTokenClaims))
 */
export function setAuthTokenGetter(getter: AuthTokenGetter | null): void {
  _tokenGetter = getter;
}

/**
 * Resolve a Bearer token using the registered getter, with the same fallback
 * order as useAuthenticatedAxios (env → ID token → access token via getter).
 */
export async function resolveAuthToken(
  getIdTokenClaims?: () => Promise<{ __raw?: string } | undefined>,
  getAccessTokenSilently?: () => Promise<string>
): Promise<string | null> {
  if (_tokenGetter) {
    return _tokenGetter();
  }

  const envToken = process.env.REACT_APP_AUTH0_TOKEN;
  if (envToken) {
    return envToken;
  }

  try {
    const idToken = await getAuthToken(getIdTokenClaims);
    if (idToken) {
      return idToken;
    }
  } catch {
    // fall through
  }

  if (getAccessTokenSilently) {
    try {
      return await getAccessTokenSilently();
    } catch {
      return null;
    }
  }

  return null;
}

/**
 * Build the standard Auth0 token getter used by AuthStateMonitor and hooks.
 */
export function createAuthTokenGetter(
  getIdTokenClaims: () => Promise<{ __raw?: string } | undefined | null>,
  getAccessTokenSilently: () => Promise<string | null>
): AuthTokenGetter {
  return async () => {
    const envToken = process.env.REACT_APP_AUTH0_TOKEN;
    if (envToken) {
      return envToken;
    }

    // getAuthToken / getIdTokenClaims return null on miss — they do not throw.
    let token = await getAuthToken(getIdTokenClaims);
    if (token) {
      return token;
    }

    try {
      return await getAccessTokenSilently();
    } catch (accessError) {
      console.warn('Failed to get Auth0 tokens:', accessError);
      return null;
    }
  };
}
